import Constants from "../../../../utils/constants.js";
import * as turf from '@turf/turf';

export const dashboardHelper = {
    /**
     * Heure de début prévue d'une tournée (`CampagneLavage.heureDebut`, chaîne « HH:MM » saisie par
     * l'exploitant, sans timezone — même règle que la Home mobile) : « 06:30 » → « 6h30 ».
     * Null / vide / invalide → null (rien d'affiché).
     */
    formatHeureDebut: (heure) => {
        if (!heure || typeof heure !== 'string') return null;
        const m = /^(\d{1,2}):(\d{2})$/.exec(heure.trim());
        if (!m) return null;
        const h = parseInt(m[1], 10);
        if (h > 23 || parseInt(m[2], 10) > 59) return null;
        return `${h}h${m[2]}`;
    },

    COLORS: [
        '#D32F2F', // red dark
        '#1976D2', // blue dark
        '#388E3C', // green dark
        '#FBC02D', // yellow dark
        '#7B1FA2', // purple
        '#0288D1', // light blue
        '#C2185B', // pink dark
        '#F57C00', // orange dark
        '#00796B', // teal dark
        '#512DA8', // deep purple
        '#455A64', // blue gray
        '#C62828', // stronger red
        '#303F9F', // indigo dark
        '#0097A7', // cyan dark
    ],

    formatDateRelative: (days) => {
        const date = new Date();
        date.setDate(date.getDate() + days);
        return date.toISOString().split('T')[0];
    },

    getDuration: (dateDebut, dateFin) => {
        if (!dateDebut) return "Non démarré";
        if (!dateFin) return "Non terminé";

        const debut = new Date(dateDebut);
        const fin = new Date(dateFin);

        const diffMs = fin - debut;
        const diffMin = Math.floor(diffMs / 60000);

        const hours = Math.floor(diffMin / 60);
        const minutes = diffMin % 60;

        return `Durée totale : ${hours}h${minutes.toString().padStart(2, "0")}`;
    },

    getClientProgress: (clientData) => {
        const total = clientData.totalALaver || 0;
        const done = (clientData.totalBacsLaves || 0) + (clientData.totalBacsImpossible || 0);

        const value = (done / total) * 100;
        return value > 100 ? 100 : value;
    },

    getStatutLabel: (data) => {
        const total = data.totalALaver || 0;
        const done = (data.totalBacsLaves || 0) + (data.totalBacsImpossible || 0);

        if (done >= total) return "Terminé";
        if (done > 0) return "En cours";
        return "Pas démarré";
    },

    getStatutColor: (data) => {
        const total = data.totalALaver || 0;
        const done = (data.totalBacsLaves || 0) + (data.totalBacsImpossible || 0);

        if (done >= total) return Constants.COLOR_THEME_GREEN;
        if (done > 0) return Constants.COLOR_THEME_BLUE;
        return "grey";
    },

    getProductiviteColor: (productivite) => {
        if (productivite >= 0 && productivite < 7) return "#FF0000"; // Rouge
        if (productivite >= 7 && productivite < 10) return "#FF8C00"; // Orange foncé
        if (productivite >= 10 && productivite < 13) return "#1976d2"; // Bleu secondaire MUI par défaut
        if (productivite >= 13) return "#D4AF37"; // Or
        return "#D3D3D3"; // Gris clair
    },

    getPolygonFromConteneurs: (conteneurs) => {
        if (!conteneurs || conteneurs.length < 3) return null;

        const points = conteneurs.map(c =>
            turf.point([parseFloat(c.longitude), parseFloat(c.latitude)])
        );
        const fc = turf.featureCollection(points);

        let polygon = turf.convex(fc); // 1 km max entre points

        if (!polygon) return null;

        return polygon.geometry.coordinates[0].map(([lng, lat]) => [lat, lng]); // latLng pour Leaflet
    },

    getTourneePolygons: (clientsData, mode) => {
        const polygons = [];

        if (mode === 'exploitant') {
            Object.values(clientsData).forEach(client => {
                Object.entries(client.operateurs).forEach(([operateurName, operateurData]) => {
                    operateurData.tours.forEach(tournee => {
                        const coordinates = dashboardHelper.getPolygonFromConteneurs(tournee.conteneurs);
                        if (coordinates) {
                            polygons.push({
                                id: tournee.id,
                                tournee: tournee,
                                color: dashboardHelper.getColorByEquipe(operateurName),
                                coordinates
                            });
                        }
                    });
                });
            });
        } else if (mode === 'client') {
            clientsData.forEach(tournee => {
                const coordinates = dashboardHelper.getPolygonFromConteneurs(tournee.conteneurs);
                if (coordinates) {
                    polygons.push({
                        id: tournee.id,
                        label: tournee.libelle,
                        color: dashboardHelper.getColorByEquipe(tournee.equipe.label),
                        coordinates
                    });
                }
            });
        }

        return polygons;
    },

    getPolygonCenter: (coordinates) => {
        const lats = coordinates.map(c => c[0]);
        const lngs = coordinates.map(c => c[1]);

        const lat = (Math.min(...lats) + Math.max(...lats)) / 2;
        const lng = (Math.min(...lngs) + Math.max(...lngs)) / 2;

        return [lat, lng];
    },

    getCoordinatesFromConteneurs: (conteneurs = []) => {
        return conteneurs
            .filter(c => c.latitude && c.longitude)
            .map(c => [parseFloat(c.latitude), parseFloat(c.longitude)]);
    },


    getColorByEquipe: (equipeName) => {
        let hash = 5381;
        for (let i = 0; i < equipeName.length; i++) {
            hash = ((hash << 5) + hash) + equipeName.charCodeAt(i); // hash * 33 + c
        }
        return dashboardHelper.COLORS[Math.abs(hash) % dashboardHelper.COLORS.length];
    }

}
